import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Alert } from '@/components/atoms/Alert';
import {
    type FeedingPen,
    type FeedingPenQuality,
    type PenQualityState,
    penLabel,
    penQualityState,
} from '@/domain/feedingDashboard';

/**
 * Pens beyond this many are counted per reason instead of named: a longer list of names
 * would outgrow the notice, and the pen table beside it names every pen anyway.
 */
const MAX_NAMED_PENS = 6;

type LeftOutState = Exclude<PenQualityState, 'ready'>;

const PEN_REASON_KEY: Record<LeftOutState, string> = {
    collecting: 'feedingModule.coverage.penCollecting',
    noData: 'feedingModule.coverage.penNoData',
    noBioscope: 'feedingModule.coverage.penNoBioscope',
};

interface BioscopeCoverageNoticeProps {
    penQuality: FeedingPenQuality;
    /** The selected pens, whose own states name the ones left out. */
    pens: readonly FeedingPen[];
    /** Display name per pen id, unique within the period. */
    names: ReadonlyMap<number, string>;
    className?: string;
}

/** Each left-out pen with its reason, in the order of the pens, as the table lists them. */
function namedReasons(
    pens: readonly FeedingPen[],
    names: ReadonlyMap<number, string>,
    t: TFunction
): string[] {
    const reasons: string[] = [];
    for (const pen of pens) {
        const state = penQualityState(pen);
        if (state === 'ready') continue;
        reasons.push(t(PEN_REASON_KEY[state], { pen: penLabel(names, pen) }));
    }
    return reasons;
}

function countedReasons(penQuality: FeedingPenQuality, t: TFunction): string[] {
    const { collecting, noData, noBioscope } = penQuality;
    return [
        collecting > 0 ? t('feedingModule.coverage.collecting', { count: collecting }) : null,
        noData > 0 ? t('feedingModule.coverage.noData', { count: noData }) : null,
        noBioscope > 0 ? t('feedingModule.coverage.noBioscope', { count: noBioscope }) : null,
    ].filter((reason): reason is string => reason !== null);
}

/**
 * Says which selected pens the Bioscope figures cover and why the others are left out
 * (IPS 2.1.4, 5). The figures come from ready pens only, so a mixed selection still has
 * Bioscope values; the notice is what keeps them from reading as the whole selection.
 *
 * It names the pens left out, since otherwise their names are only in the KPI tooltips
 * and the cards' "3 of 5 pens" cannot be checked against the selection. Readiness itself
 * is the API's (O3): the counts come from the summary, and the names from each pen's own
 * state by the same rule.
 */
export function BioscopeCoverageNotice(props: BioscopeCoverageNoticeProps) {
    const { penQuality, pens, names, className } = props;
    const { t, i18n } = useTranslation();
    const { ready, collecting, noData, noBioscope } = penQuality;
    const total = ready + collecting + noData + noBioscope;
    if (total === 0 || ready === total) return null;

    const named = namedReasons(pens, names, t);
    // With no pen to name, "Left out: ." would stand alone; only pens and a summary that
    // disagree get there, and the counts still say something true.
    const reasons =
        named.length > 0 && named.length <= MAX_NAMED_PENS ? named : countedReasons(penQuality, t);
    // The locale's own list punctuation rather than a separator hardcoded here.
    const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(reasons);

    const sentences = [
        ready === 0
            ? t('feedingModule.coverage.none', { count: total })
            : t('feedingModule.coverage.partial', { covered: ready, count: total }),
        t('feedingModule.coverage.leftOut', { reasons: list }),
        collecting > 0 ? t('feedingModule.coverage.collectingHint') : null,
        noData > 0 ? t('feedingModule.coverage.noDataHint') : null,
    ].filter((sentence): sentence is string => sentence !== null);

    // Info, not warning: pens still collecting data are an expected state, so this must not
    // look like the feeding-service failure beside it or the four amber warning triggers.
    return (
        <Alert tone="info" size="small" live="off" className={className}>
            {sentences.join(' ')}
        </Alert>
    );
}
